/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { cloneDeep } from 'lodash';
import { DateTime } from 'luxon';
import { TFunction } from 'i18next';
import { Constants } from '@pwc/pwc-deals-web-common';
import {
  updateGroupOrder,
  updateAnalysesSortOrder,
  updateGroupName,
  deleteGroup,
  deleteAnalysis,
  deleteAnalyses,
  getGroups,
  updateAnalysesSortOrderKfa,
} from '../services/groupsService';
import { GroupMoveDirection } from '../models/constants';
import Group from '../models/Group';
import SelectedGroups from '../models/SelectedGroups';
import { MoveAnalysisAPIRequest } from '../models/MoveAnalysisAPIRequest';
import GroupedAnalysis from '../models/GroupedAnalysis';
import { CONTENT_SUBTYPES } from '../../../common/engine/utilities/constants';
import {
  viewAnalysisPath,
  viewStructuredAnalysisPathForSubType,
  viewHypothesisPath,
  editAnalysisPath,
  editStructuredAnalysisDocument,
  viewStructuredAnalysis2PathForSubType,
  isStructuredAnalysis2ContentType,
  editStructuredAnalysis2PathForSubType,
} from '../../../common/services/urlService';
import { MoveAnalysisAPIRequestKfa } from '../models/MoveAnalysisAPIRequestKfa';

export const formatTitle = (title: string): string =>
  title.toLowerCase().split(' ').join('-');

const getGroupIndexFromGroupId = (groupId: number, groups: Group[]): number => {
  return groups.findIndex((g) => {
    let id: number | undefined = g.groupId;
    if (!id) {
      id = g.contentThreadId;
    }
    return id === groupId;
  });
};
const getGroupIndexFromTitle = (
  groupTitle: string,
  groups: Group[]
): number => {
  return groups.findIndex((g) => formatTitle(g.title) === groupTitle);
};

export const getContentMessage = (
  contentCount: number,
  t: TFunction
): string => {
  return `${contentCount} ${
    contentCount === 1 ? t('analysis') : t('analyses')
  }`;
};

export const clearGroupsSelection = (groups: Group[]): Group[] => {
  for (let i = 0; i < groups.length; i += 1) {
    for (let j = 0; j < groups[i].items.length; j += 1) {
      groups[i].items[j].isSelected = false;
    }
  }
  return groups;
};

export const replaceGroupName = (
  id: number,
  title: string,
  replaceWith: string
): string => (id === -1 ? replaceWith : title);

export const updateSortOrder = (groups: Group[]): void => {
  for (let i = 0; i < groups.length; i += 1) {
    const group = groups[i];
    group.sortOrder = i;
    if (group.items && group.items.length) {
      for (let j = 0; j < group.items.length; j += 1) {
        const item = group.items[j];
        item.sortOrder = j;
      }
    }
  }
};

export const getAnalysesToMove = (
  groups: Group[],
  selectedItems: number[],
  destinationGroupId: number,
  destinationIndex = 0
): MoveAnalysisAPIRequest[] => {
  let toOrderNumber = destinationIndex;
  const analysesToMove: MoveAnalysisAPIRequest[] = [];
  const analysesList: GroupedAnalysis[] = [];
  // eslint-disable-next-line no-restricted-syntax
  for (const group of groups) {
    // eslint-disable-next-line no-loop-func
    selectedItems.forEach((id) => {
      const index = group.items.findIndex((i) => i.contentThreadId === id);
      if (index > -1) {
        const analysis = group.items[index];
        const { contentThreadId, sortOrder } = analysis;
        analysesToMove.push({
          contentThreadId,
          toGroupId: destinationGroupId,
          toOrderNumber,
          fromGroupId: group.groupId,
          fromOrderNumber: sortOrder,
        });
        analysesList.push(analysis);
        group.items.splice(index, 1);
        group.contentCount -= 1;
        toOrderNumber += 1;
      }
    });
  }
  let insertIndex = destinationIndex;
  const groupIndex = getGroupIndexFromGroupId(destinationGroupId, groups);
  // eslint-disable-next-line no-restricted-syntax
  for (const analysis of analysesList) {
    groups[groupIndex].items.splice(insertIndex, 0, analysis);
    insertIndex += 1;
    groups[groupIndex].contentCount += 1;
  }
  return analysesToMove;
};

export const getAnalysesToMoveKfa = (
  groups: Group[],
  selectedItems: number[],
  destinationGroupId: number,
  destinationIndex = 0
): MoveAnalysisAPIRequestKfa[] => {
  let toOrderNumber = destinationIndex;
  const analysesToMove: MoveAnalysisAPIRequestKfa[] = [];
  const analysesList: GroupedAnalysis[] = [];
  // eslint-disable-next-line no-restricted-syntax
  for (const group of groups) {
    // eslint-disable-next-line no-loop-func
    selectedItems.forEach((id) => {
      const index = group.items.findIndex((i) => i.contentThreadId === id);
      if (index > -1) {
        const analysis = group.items[index];
        const { contentThreadId, sortOrder } = analysis;
        analysesToMove.push({
          kfaContentThreadId: destinationGroupId,
          contentThreadId,
          toOrderNumber,
          fromOrderNumber: sortOrder,
        });
        analysesList.push(analysis);
        group.items.splice(index, 1);
        group.contentCount -= 1;
        toOrderNumber += 1;
      }
    });
  }
  let insertIndex = destinationIndex;
  const groupIndex = getGroupIndexFromGroupId(destinationGroupId, groups);
  // eslint-disable-next-line no-restricted-syntax
  for (const analysis of analysesList) {
    groups[groupIndex].items.splice(insertIndex, 0, analysis);
    insertIndex += 1;
    groups[groupIndex].contentCount += 1;
  }
  return analysesToMove;
};

export const saveAnalyses = async (
  projectId: string | number,
  groups: Group[],
  contentThreadIds: number[],
  toGroupId = -1,
  destinationIndex = 0,
  isKfa = false
): Promise<void> => {
  let analysesToMove = [];
  if (isKfa) {
    analysesToMove = getAnalysesToMoveKfa(
      groups,
      contentThreadIds,
      toGroupId,
      destinationIndex
    );
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    await updateAnalysesSortOrderKfa(projectId, analysesToMove);
  } else {
    analysesToMove = getAnalysesToMove(
      groups,
      contentThreadIds,
      toGroupId,
      destinationIndex
    );
    await updateAnalysesSortOrder(projectId, analysesToMove);
  }
};

export const moveAnalyses = async (
  projectId: number | string,
  groups: Group[],
  groupTitle: string,
  selectedIDs: number[]
): Promise<Group[] | undefined> => {
  try {
    const movedGroups = cloneDeep(groups);
    const destGroupIdx = getGroupIndexFromTitle(
      formatTitle(groupTitle),
      groups
    );
    const destinationGroupId = groups[destGroupIdx].groupId;
    await saveAnalyses(projectId, movedGroups, selectedIDs, destinationGroupId);
    updateSortOrder(movedGroups);
    return movedGroups;
  } catch (error) {
    return undefined;
  }
};

export const moveCustomAnalysisGroup = async (
  groups: Group[],
  groupId: number,
  direction: GroupMoveDirection,
  projectId: string | number
): Promise<Group[] | undefined> => {
  try {
    const clonedGroups = cloneDeep(groups);
    const index = clonedGroups.findIndex((g) => g.groupId === groupId);
    const group = clonedGroups[index];
    clonedGroups.splice(index, 1);
    clonedGroups.splice(direction === 1 ? index + 1 : index - 1, 0, group);
    updateSortOrder(clonedGroups);
    await updateGroupOrder(projectId, groupId, direction);
    return clonedGroups;
  } catch (error) {
    return undefined;
  }
};

export const deleteMultipleAnalyses = async (
  projectId: string,
  groups: Group[],
  selectedIds: number[]
): Promise<Group[] | undefined> => {
  try {
    await deleteAnalyses(projectId, selectedIds);
    groups.forEach((g) => {
      selectedIds.forEach((id) => {
        const analysis = g.items.find((a) => a.contentThreadId === id);
        if (analysis) {
          g.contentCount -= 1;
          g.items = g.items.filter(
            (a) => a.contentThreadId !== analysis.contentThreadId
          );
        }
      });
    });
    const clonedGroups = cloneDeep(groups);
    updateSortOrder(clonedGroups);
    return clonedGroups;
  } catch (e) {
    return undefined;
  }
};

export const renameGroup = async (
  projectId: string,
  groups: Group[],
  newName: string,
  groupId: number
): Promise<Group[] | undefined> => {
  try {
    const success = await updateGroupName(projectId, newName, groupId);
    if (success) {
      const clonedGroups = cloneDeep<Group[]>(groups);

      return clonedGroups.map((g) => {
        if (g.groupId === groupId) {
          g.title = newName;
        }
        return g;
      });
    }
    return groups;
  } catch (error) {
    return undefined;
  }
};

export const getSelectedIds = (
  groups: Group[],
  contentThreadId: number,
  selected: boolean
): SelectedGroups => {
  const clonedGroups = cloneDeep(groups);
  const selectedIds: number[] = [];
  clonedGroups.forEach((group) => {
    group.items.forEach((item) => {
      if (item.contentThreadId === contentThreadId) {
        item.isSelected = selected;
      }
      if (item.isSelected) {
        selectedIds.push(item.contentThreadId);
      }
    });
  });
  return { updatedGroups: clonedGroups, updatedSelectedIDs: selectedIds };
};

export const deselectAllAnalyses = (groups: Group[]): SelectedGroups => {
  const clonedGroups = cloneDeep(groups);
  clonedGroups.forEach((group) => {
    group.items.forEach((item) => {
      item.isSelected = false;
    });
  });
  return { updatedGroups: clonedGroups, updatedSelectedIDs: [] };
};

export const deleteSingleGroup = async (
  projectId: string,
  groupId: number
): Promise<Group[] | undefined> => {
  try {
    const success = await deleteGroup(projectId, groupId);
    if (success) {
      const groupsList = await getGroups(projectId);
      return groupsList;
    }
    return undefined;
  } catch (error) {
    return undefined;
  }
};

export const deleteSingleAnalysis = async (
  projectId: string,
  groups: Group[],
  contentThreadId: number
): Promise<Group[] | undefined> => {
  const success = await deleteAnalysis(projectId, contentThreadId);
  if (success) {
    // eslint-disable-next-line no-restricted-syntax
    for (const group of groups) {
      const analysesList = group.items || [];
      const analysis = analysesList.find(
        (a) => a.contentThreadId === contentThreadId
      );
      if (analysis) {
        group.items = analysesList.filter(
          (item) => item.contentThreadId !== contentThreadId
        );
        group.contentCount -= 1;
        break;
      }
    }
    const clonedGroups = cloneDeep<Group[]>(groups);
    updateSortOrder(clonedGroups);
    return clonedGroups;
  }
  return undefined;
};

export const getNavigationLink = (
  projectId: number,
  contentThreadId: number,
  subType: number,
  contentType: number,
  isPwcUser: boolean
): string => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-call
  if (isStructuredAnalysis2ContentType(contentType)) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-return
    return viewStructuredAnalysis2PathForSubType({
      projectId,
      contentThreadId,
      isPwcUser,
    });
  }

  switch (subType) {
    case CONTENT_SUBTYPES.UNSTRUCTURED:
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      return viewAnalysisPath(projectId, contentThreadId);
    case CONTENT_SUBTYPES.QUALITY_OF_EARNINGS:
    case CONTENT_SUBTYPES.NET_WORKING_CAPITAL:
    case CONTENT_SUBTYPES.DEBT_DEBT_LIKE:
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      return viewStructuredAnalysisPathForSubType(projectId, subType);
    default:
      throw new Error('Document Subtype was not found');
  }
};

export const getEditNavigationLink = (
  projectId: number,
  contentThreadId: number,
  subType: number,
  contentType: number
): string => {
  if (isStructuredAnalysis2ContentType(contentType)) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-return
    return editStructuredAnalysis2PathForSubType({
      projectId,
      contentThreadId,
    });
  }
  switch (subType) {
    case CONTENT_SUBTYPES.UNSTRUCTURED:
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      return editAnalysisPath(projectId, contentThreadId);
    case CONTENT_SUBTYPES.QUALITY_OF_EARNINGS:
      return editStructuredAnalysisDocument(
        projectId,
        Constants.QUALITY_OF_EARNINGS
      );
    case CONTENT_SUBTYPES.NET_WORKING_CAPITAL:
      return editStructuredAnalysisDocument(
        projectId,
        Constants.NET_WORKING_CAPITAL
      );
    case CONTENT_SUBTYPES.DEBT_DEBT_LIKE:
      return editStructuredAnalysisDocument(
        projectId,
        Constants.DEBT_AND_DEBT_LIKE
      );
    default:
      throw new Error('Document Subtype was not found');
  }
};

export const geKFAtNavigationLink = (
  projectId: number,
  hypothesisId: number
): string => {
  return viewHypothesisPath(projectId, hypothesisId);
};

export const formatDate = (date: string): string => {
  const formattedDate: string = DateTime.fromISO(date, {
    zone: 'utc',
  })
    .toLocal()
    .toFormat('H:mm ZZZZ - d LLL yyyy');

  return formattedDate;
};
